import DOMPurify from 'dompurify';

interface ProcessTemplateOptions {
  content: string;
  data?: Record<string, string>;
  errorIconSrc: string;
  mappedValueClass: string;
  errorClass: string;
  isHtmlContent?: boolean; // false for SMS (plain text), true for Email
}

export function processTemplateContent({
  content,
  data = {},
  errorIconSrc,
  mappedValueClass,
  errorClass,
  isHtmlContent = false,
}: ProcessTemplateOptions): string {
  // 1. Prepare Content
  // If it's SMS (plain text), we must convert newlines to <br> BEFORE processing
  // to prevent them from being lost in the final HTML render.
  const preparedContent = isHtmlContent ? content : content.replace(/\n/g, '<br/>');

  const PLACEHOLDER_REGEX = /{{\s*([^{}\n\r]+?)\s*}}/g;

  const processedHtml = preparedContent.replace(PLACEHOLDER_REGEX, (_, inner) => {
    const key = inner.trim();

    // Check if key exists in mapping data
    if (key in data) {
      const val = data[key];

      // Edge Case: Value is empty/null -> Render Error
      if (val === null || val === undefined || val === '') {
        return getErrorHtml(key, errorIconSrc, errorClass);
      }

      // DETERMINISTIC CHECK:
      // We know it is a Mapped Context Attribute if the *value itself*
      // is wrapped in double curly braces.
      if (val.trim().startsWith('{{') && val.trim().endsWith('}}')) {
        return `<span class="${mappedValueClass}">${val}</span>`;
      }

      // Default: It is a standard text value (e.g. "John")
      return val;
    }

    // No mapping found: Return original string (e.g. {{Something}})
    return `{{${key}}}`;
  });

  // 2. Sanitize final result
  // We sanitize specific tags to allow styling but prevent scripts
  return DOMPurify.sanitize(processedHtml, {
    ADD_TAGS: ['style'],
    WHOLE_DOCUMENT: true,
  });
}

function getErrorHtml(key: string, iconSrc: string, baseClass: string): string {
  return `<span title="Placeholder mapping needs to be corrected" class="${baseClass}">
            <span class="${baseClass}__text">{{${key}}}</span>
            <span class="${baseClass}__icon">
                <img src="${iconSrc}" alt="Placeholder error" />
            </span>
          </span>`;
}
